import React, { useMemo, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../store";
import { useAttractions, type Attraction } from "../../services/attractionService";

interface AttractionsProps {
  title?: string;
  subtitle?: string;
  attractions?: Attraction[];
  showViewAll?: boolean;
  onViewAll?: () => void;
  onAttractionClick?: (attraction: Attraction) => void;
}

const Attractions: React.FC<AttractionsProps> = ({
  title,
  subtitle,
  attractions: propAttractions,
  showViewAll = true,
  onViewAll,
  onAttractionClick,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const [isLoaded, setIsLoaded] = useState(false);

  // Fetch attractions from API if not provided as props
  const { data: attractionsResponse, isLoading } = useAttractions({
    locale: currentLanguage,
    per_page: 6,
    featured: true,
  });

  const apiAttractions = attractionsResponse?.attractions || [];

  // Default attractions with translations (fallback)
  const defaultAttractions = useMemo(
    () => [
      {
        id: "1",
        title: t("attractions.beach.title", "Beautiful Beach"),
        description: t(
          "attractions.beach.description",
          "Relax on pristine white sands and enjoy crystal-clear waters perfect for swimming and water sports."
        ),
        image: "/images/beach.jpg",
        location: t("attractions.beach.location", "Coastal Area"),
        category: t("attractions.categories.beach", "Beach"),
      },
      {
        id: "2",
        title: t("attractions.mountain.title", "Mountain Views"),
        description: t(
          "attractions.mountain.description",
          "Experience breathtaking mountain landscapes with hiking trails and panoramic viewpoints."
        ),
        image: "/images/mountain.jpg",
        location: t("attractions.mountain.location", "Mountain Region"),
        category: t("attractions.categories.mountain", "Mountain"),
      },
      {
        id: "3",
        title: t("attractions.cultural.title", "Cultural Heritage"),
        description: t(
          "attractions.cultural.description",
          "Discover rich cultural heritage sites with historical monuments and traditional architecture."
        ),
        image: "/images/cultural.jpg",
        location: t("attractions.cultural.location", "Historic District"),
        category: t("attractions.categories.cultural", "Cultural"),
      },
    ],
    [t]
  );

  // Use props if provided, otherwise use API data, otherwise use defaults
  const displayAttractions = propAttractions || (apiAttractions.length > 0 ? apiAttractions : defaultAttractions);

  useEffect(() => {
    if (!isLoading) {
      setIsLoaded(true);
    }
  }, [isLoading]);

  const handleViewAll = () => {
    if (onViewAll) {
      onViewAll();
    } else {
      navigate(`/${currentLanguage}/attractions`);
    }
  };

  return (
    <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-white via-emerald-50/50 to-stone-100/50 dark:from-gray-800 dark:via-emerald-900/10 dark:to-stone-900/10">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-200/20 dark:bg-emerald-900/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-stone-200/20 dark:bg-stone-900/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={
              isLoaded ? { scale: 1, opacity: 1 } : { scale: 0.9, opacity: 0 }
            }
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-block mb-6"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-emerald-800 dark:text-emerald-300 rounded-full text-sm font-semibold border border-emerald-200 dark:border-emerald-800 shadow-lg">
              <span className="text-lg">🏖️</span>
              <span>{t("attractions.badge", "Explore Our Region")}</span>
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 dark:text-white mb-6 leading-tight tracking-tight"
          >
            {title || t("attractions.title", "Touristic Attractions")}
            <span className="block mt-2 bg-gradient-to-r from-emerald-600 via-stone-500 to-emerald-700 bg-clip-text text-transparent">
              {t("attractions.titleAccent", "Discover Beauty")}
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto"
          >
            {subtitle ||
              t(
                "attractions.subtitle",
                "Explore the most beautiful destinations and attractions in our region"
              )}
          </motion.p>
        </motion.div>

        {/* Attractions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-10">
          {displayAttractions.map((attraction, index) => (
            <motion.div
              key={attraction.id}
              initial={{ opacity: 0, y: 50 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              whileHover={{ y: -12, scale: 1.03 }}
              className="group relative"
            >
              {/* Floating gradient glow */}
              <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 via-stone-500 to-emerald-600 rounded-3xl opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-500"></div>

              {/* Main Card */}
              <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 overflow-hidden h-full">
                {/* Image Section */}
                <div className="relative h-64 overflow-hidden">
                  <div
                    className="w-full h-full bg-center bg-no-repeat bg-cover transition-transform duration-500 group-hover:scale-110"
                    style={{ backgroundImage: `url('${attraction.image}')` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                  {/* Category Badge */}
                  {attraction.category && (
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-white/20 backdrop-blur-md text-white border border-white/30">
                        {attraction.category}
                      </span>
                    </div>
                  )}

                  {/* Location Badge */}
                  {attraction.location && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={
                        isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }
                      }
                      transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                      className="absolute bottom-4 left-4 right-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl px-4 py-2 shadow-lg border border-white/50 dark:border-gray-700/50"
                    >
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-emerald-600 dark:text-emerald-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                          {attraction.location}
                        </span>
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Content Section */}
                <div className="p-6 lg:p-8">
                  <h3 className="text-xl lg:text-2xl font-extrabold text-gray-900 dark:text-white mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-emerald-600 group-hover:to-stone-600 transition-all duration-300 line-clamp-2">
                    {attraction.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm lg:text-base leading-relaxed mb-6 line-clamp-3">
                    {attraction.description}
                  </p>

                  {/* Action Button */}
                  <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      if (onAttractionClick) {
                        onAttractionClick(attraction);
                      } else {
                        navigate(`/${currentLanguage}/attractions/${attraction.id}`);
                      }
                    }}
                    className="w-full px-4 py-3 bg-gradient-to-r from-emerald-600 to-stone-600 hover:from-emerald-700 hover:to-stone-700 text-white rounded-xl font-bold transition-all duration-200 text-sm shadow-lg hover:shadow-xl"
                  >
                    {t("attractions.explore", "Explore")} →
                  </motion.button>
                </div>

                {/* Decorative gradient corner */}
                <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-gradient-to-br from-emerald-500 to-stone-500 opacity-10 group-hover:opacity-20 rounded-full blur-2xl transition-opacity duration-500"></div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View All Button */}
        {showViewAll && displayAttractions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-center mt-20"
          >
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleViewAll}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-800 text-gray-900 dark:text-white rounded-2xl font-bold text-lg transition-all duration-200 shadow-xl hover:shadow-2xl border-2 border-gray-200 dark:border-gray-700"
            >
              {t("attractions.viewAll", "View All Attractions")}
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </motion.button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Attractions;
